import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { trackError, ValidationError } from '#api/errors.ts';
import { useVerifyGoogleIdP } from '#api/google-idp.queries.ts';
import { describeGoogleIdPError } from '#api/google-idp.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { takePendingGoogleIdPFlow } from './pending-flow.ts';

type Failure = { message: string; providerId: string | null };

/** There is no pending flow in this tab: the link was reopened, bookmarked, or already used. */
class NoPendingFlowError extends Error {}

/**
 * {@link GoogleIdPCallbackPage} is where Google sends the admin back after the consent screen. It hands the
 * whole callback URL to the api to finish the connection, then returns the admin to the provider page
 * they started from.
 */
export function GoogleIdPCallbackPage() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { mutateAsync: verify } = useVerifyGoogleIdP();
  const [failure, setFailure] = useState<Failure | null>(null);
  // The request token is single use, so verify must run exactly once, even under StrictMode's double effects.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const callbackURL = new URL(pathname + search, globalThis.location.origin).toString();
    const pending = takePendingGoogleIdPFlow();

    // The code and state in the address bar only work once; don't leave them in history.
    void navigate(pathname, { replace: true });

    async function finish() {
      if (!pending) {
        throw new NoPendingFlowError();
      }
      const connection = await verify({ location: callbackURL, requestToken: pending.requestToken });
      await navigate(`/settings/sso/${connection.endpointOIDCProviderID}`, {
        replace: true,
        state: { googleIdPConnected: true },
      });
    }

    finish().catch((error: unknown) => {
      if (error instanceof NoPendingFlowError) {
        setFailure({
          message: 'This connection attempt expired or was already used. Start again from the provider page.',
          providerId: null,
        });
        return;
      }
      // Reworded validation errors are expected outcomes (consent denied, not an admin, ...).
      if (!(error instanceof ValidationError)) {
        trackError(error);
      }
      setFailure({ message: describeGoogleIdPError(error), providerId: pending?.providerId ?? null });
    });
  }, [navigate, verify, pathname, search]);

  return (
    <MainLayout header={<PageHeader title="Google Workspace" />}>
      {failure ?
        <>
          <ErrorMessage>{failure.message}</ErrorMessage>
          <p className="p mt-24">
            <Link to={failure.providerId ? `/settings/sso/${failure.providerId}` : '/settings/sso?tab=endpoint'}>
              Back to single sign-on settings
            </Link>
          </p>
        </>
      : <p className="p">Finishing the connection with Google…</p>}
    </MainLayout>
  );
}
